import { ToolOutlined } from '@ant-design/icons';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 80px 40px 40px 40px;
  min-height: calc(100vh - 70px);
  background: var(--color-surface, #f3f7f8);
`;

const Card = styled.div`
  width: 100%;
  max-width: 640px;
  background: var(--color-surface-raised, #ffffff);
  border-radius: 20px;
  box-shadow: 10px 10px 30px 0px #0000001a;
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: Inter;
`;

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--color-status-warning-bg, #fff5e2);
  color: #ffbc3e;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 34px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  color: var(--color-text-dark, #042a2a);
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
`;

const Description = styled.p`
  margin: 0 0 18px 0;
  color: #5a6a6a;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  max-width: 480px;
`;

const SupportNote = styled.p`
  margin: 0;
  color: #5a6a6a;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  max-width: 480px;

  a {
    color: #1d2440;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const BannerWrapper = styled.div<{ $width?: string }>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: var(--color-status-warning-bg, #fff5e2);
  border: 1px solid #ffbc3e;
  border-radius: 10px;
  padding: 14px 18px;
  margin: 0 0 16px 0;
  font-family: Inter;
  width: ${(props) => props.$width || '100%'};
  box-sizing: border-box;
`;

const BannerIcon = styled.div`
  color: #ffbc3e;
  font-size: 22px;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 2px;
`;

const BannerText = styled.div`
  color: var(--color-text-dark, #042a2a);
  font-size: 13px;
  line-height: 20px;

  strong {
    font-weight: 600;
  }

  a {
    color: #1d2440;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

interface VatCheckerUnavailableBannerProps {
  width?: string;
}

export const VatCheckerUnavailableBanner: React.FC<
  VatCheckerUnavailableBannerProps
> = ({ width }) => {
  const { t } = useTranslation();
  return (
    <BannerWrapper $width={width}>
      <BannerIcon>
        <ToolOutlined />
      </BannerIcon>
      <BannerText>
        <strong>{t('vat-checker.temporarily-unavailable-title')}</strong>{' '}
        {t('vat-checker.temporarily-unavailable-banner-description')}{' '}
        <a href="mailto:helpme@generate.tax">helpme@generate.tax</a>.
      </BannerText>
    </BannerWrapper>
  );
};

export const VatCheckerUnavailablePage: React.FC = () => {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('navigation.vat-checker')} | ${process.env.PROJECT_NAME}`}</title>
        <meta
          name="description"
          content={t('vat-checker.temporarily-unavailable-page-title')}
        />
      </Helmet>
      <Wrapper>
        <Card>
          <IconCircle>
            <ToolOutlined />
          </IconCircle>
          <Title>{t('vat-checker.temporarily-unavailable-page-title')}</Title>
          <Description>
            {t('vat-checker.temporarily-unavailable-description')}
          </Description>
          <SupportNote>
            {t('vat-checker.temporarily-unavailable-support-note')}{' '}
            <a href="mailto:helpme@generate.tax">helpme@generate.tax</a>.
          </SupportNote>
        </Card>
      </Wrapper>
    </React.Fragment>
  );
};
